'use client'

import {Button, Pastil, SanityImage, SmallText} from '@local/ui/components'
import {cx} from '@local/utils/cx'

import {SanityImageType} from '@local/types'
import {ForwardedRef, forwardRef} from 'react'
import {formatPrice} from '@local/utils/currency'

export interface RelatedCardProps {
	className?: string;
	imageTag?: { text: string; variant: 'filled' | 'outlined' };
	keywords?: string;
	image: SanityImageType;
	item: {
		slug: string;
		title: string;
		_id: string;
		type:
		| 'merch'
		| 'article'
		| 'experience'
		| 'page'
		| 'leadership'
		| 'destination';
		location?: string;
		datePeriod?: string;
		productType?: string;
		excerpt?: string;
		price?: string;
		store?: {
			priceRange: {
				minVariantPrice: string;
				maxVariantPrice: string;
			};
		};
	};
	discoverLabel?: string;
	locationLabel?: string;
	index: number;
	isHighlight: boolean;
	smallMobile: boolean;
}

export const RelatedCard = forwardRef(
	(
		{
			className,
			keywords,
			image,
			imageTag,
			discoverLabel,
			item,
			locationLabel,
			isHighlight,
			smallMobile,
		}: RelatedCardProps,
		ref: ForwardedRef<HTMLElement>,
	) => {
		const slug =
      item.type === 'page' ? `/${item.slug}` : `/${item.type}/${item.slug}`
		return (
			<article
				ref={ref}
				className={cx(
					isHighlight && '!span-w-10 lg:!span-w-4 mx-auto',
					smallMobile && 'lg-max:span-w-6',
					'shrink-0 flex flex-row items-end rounded-lg   justify-center relative span-w-7 lg:w-[calc(2.5*var(--column)+1.5*var(--gutter))] overflow-clip group',
					className,
				)}>
				<Button href={slug} className='outline-none group/btn w-full'>
					<SanityImage
						image={image}
						alt={item.title}
						width={268}
						height={400}
						desktopWidth={268}
						className={cx(
							isHighlight && 'aspect-square',
							'w-full object-cover',
							'transition-transform transform-gpu duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
							'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
						)}
						desktopOnly />
					<SanityImage
						image={image}
						alt={item.title}
						width={200}
						height={300}
						mobileWidth={200}
						className={cx(
							isHighlight && 'aspect-square',
							'w-full object-cover',
							'transition-transform transform-gpu duration-700 ease-[cubic-bezier(.35,0,.19,1)]',
							'scale-110 group-hover:scale-100 group-focus-visible/btn:scale-100',
						)}
						mobileOnly />
					<div
						className={cx(
							'absolute inset-0 flex flex-col items-center justify-center text-white bg-black/30 px-12 lg:px-30',
							'transition-[backdrop-filter,background-color] transform-gpu duration-700 ease-quad-out group-hover:backdrop-blur-sm group-hover:bg-black/50',
							'group-focus-visible/btn:backdrop-blur-sm group-focus-visible/btn:bg-black/50',
						)}>
						<h3 className='font-serif text-16 lg:text-20 tracking-tighter leading-snug mb-10 capitalize'>
							{item.title}
						</h3>
						<SmallText textColor='white'>
							{item.location && (
								<>
									<span>{locationLabel}: </span>
									<span>{item.location}</span>
								</>
							)}
							{item.productType && <span>{item.productType}</span>}
						</SmallText>
						{item.price && (
							<SmallText textColor='white' className='mt-20'>
								{formatPrice(item.price.substring(1), 'nl-NL', undefined, {
									minimumFractionDigits: 0,
									maximumFractionDigits: 0,
								}).replace(/\s/g, '')}
							</SmallText>
						)}
						{item.datePeriod && (
							<SmallText textColor='white'>{item.datePeriod}</SmallText>
						)}
					</div>
					{imageTag?.text && (
						<Pastil
							variant={imageTag.variant}
							className='absolute top-20 left-1/2 -translate-x-1/2'>
							{imageTag.text}
						</Pastil>
					)}
					{discoverLabel && (
						<Button
							variant='underlined'
							active
							className='absolute bottom-20 text-white text-10 leading-none left-1/2 -translate-x-1/2'>
							{discoverLabel}
						</Button>
					)}
					{item.excerpt && item.store && (
						<SmallText
							textColor='white'
							className='absolute leading-none left-1/2 -translate-x-1/2 bottom-20 w-full'>
							{item.excerpt +
                formatPrice(item.store?.priceRange.minVariantPrice, 'nl-NL', undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0},
                ).replace(/\s/g, '')}
						</SmallText>
					)}
					{keywords && (
						<SmallText
							textColor='white'
							className='absolute leading-none left-1/2 -translate-x-1/2 bottom-20 w-full'>
							{keywords}
						</SmallText>
					)}
				</Button>
			</article>
		)
	},
)
